import { AssignableTrainingPlan, instanceOfAssignableTrainingPlan, DeckDTO, TrainingPlan } from '@/generated';
import { Box, Card, CardActionsProps, CardContent, Grow, Link, Stack, Tooltip, Typography } from '@mui/material';
import { ReactElement, useContext } from 'react';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useNavigate } from 'react-router-dom';
import { Visibility } from '@mui/icons-material';

import { isPlanEditor, isPlanViewer } from './utils';
import { AppContext } from '@/contexts/app.context';
import { pathMap } from '@/navigation/paths';

export const PlanCard = (props: { plan: TrainingPlan | AssignableTrainingPlan; decks?: DeckDTO[]; children?: ReactElement<CardActionsProps>; timeout: number }) => {
  const { plan, children } = props;
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const user = context.state?.userProfile;
  const setIds = Array.from(new Set(plan.units.flatMap((g) => g.workouts.flatMap((wu) => wu.deckId))));
  let missingIds: Array<string> = [];
  let userHasSets = undefined;

  if (instanceOfAssignableTrainingPlan(plan)) {
    missingIds = plan.missingDeckIds;
    userHasSets = plan.userHasDecks;
  }

  const border = { borderColor: userHasSets ? 'primary.dark' : 'red', borderStyle: userHasSets !== undefined ? 'solid' : 'none', borderWidth: 1 };

  return (
    <Grow in={true} timeout={props.timeout}>
      <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...border }} className="h-100">
        <CardContent sx={{ flex: 0.9 }} onClick={() => navigate(`/plan-viewer/${plan.id}`)}>
          <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
            {plan?.title}
          </Typography>
          <Stack direction="row">
            <Typography sx={{ fontSize: 14, justifyContent: 'center', alignContent: 'center' }} color="text.secondary" gutterBottom>
              {plan.units.length} Units
            </Typography>
          </Stack>
          <Stack sx={{ mt: 1 }} direction="row">
            {isPlanEditor(plan, user) && (
              <Tooltip title={`You are the creator of ${plan.title} and may modify the plan.`}>
                <WorkspacePremiumIcon sx={{ mr: 1 }} />
              </Tooltip>
            )}

            {isPlanViewer(plan, user) && (
              <Tooltip title={`You may view ${plan.title}.`}>
                <Visibility />
              </Tooltip>
            )}
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{}} variant="body2">
              {plan?.description || 'No Description'}
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography>Decks used in this plan:</Typography>
            <Typography sx={{ m: 1 }} variant="body2">
              {setIds.map((sid) => (
                <Link sx={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <Typography
                    onClick={(e) => {
                      navigate(`${pathMap.Decks}/${sid}`);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    sx={{ fontStyle: 'italic', color: missingIds.includes(sid) ? 'red' : undefined }}
                  >
                    - {props.decks?.find((s) => s.id === sid)?.name}
                  </Typography>
                </Link>
              ))}
            </Typography>
          </Box>
        </CardContent>
        {children}
      </Card>
    </Grow>
  );
};
